import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { confirmBonusReceipt } from "../../api/bonuses";

// نافذة صريحة: توجّه إلى (المصدر) لاستلام مكافأتك — هل استلمتها؟ نعم (أخضر) / لم أستلم (أحمر).
export const BonusReceiptDialog = ({ bonus, onDone, onLater }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");

  const act = async (confirmed) => {
    setBusy(true);
    try {
      await confirmBonusReceipt(bonus.bonus_id, confirmed, confirmed ? null : note.trim() || null);
      toast[confirmed ? "success" : "warning"](t(confirmed ? "bonus.confirmDone" : "bonus.rejectDone"));
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || (typeof detail === "string" ? detail : t("bonus.actionFailed")));
    }
    setBusy(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4"
         data-testid="bonus-receipt-dialog">
      <NeuCard className="grid w-full max-w-md gap-4">
        <div className="grid gap-1">
          <p className="text-xs font-black text-primary">{t("bonus.receipt.badge")}</p>
          <h3 className="text-lg font-black leading-snug">
            {t("bonus.receipt.question", { source: bonus.payment_source_name })}
          </h3>
        </div>
        <div className="grid gap-2 rounded-2xl bg-secondary/60 p-4">
          <p className="num text-3xl font-black text-success" data-testid="bonus-receipt-amount">
            {bonus.amount} {bonus.currency}
          </p>
          <p className="text-xs"><b>{t("bonus.fields.source")}:</b> {bonus.payment_source_name}</p>
          <p className="text-xs"><b>{t("bonus.fields.reason")}:</b> {bonus.reason}</p>
          <p className="num font-mono text-[11px] text-muted-foreground" dir="ltr">{bonus.serial_number}</p>
        </div>
        {rejecting ? (
          <label className="grid gap-1 text-xs font-bold">
            {t("bonus.receipt.noteLabel")}
            <NeuInput data-testid="bonus-reject-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder={t("bonus.receipt.notePlaceholder")} />
          </label>
        ) : null}
        <div className="grid gap-2 sm:grid-cols-2">
          <NeuButton disabled={busy} data-testid="bonus-receipt-yes-btn" onClick={() => act(true)}
                     className="flex items-center justify-center gap-2 bg-success/15 py-3 font-black text-success">
            <CheckCircle2 className="h-4 w-4" /> {t("bonus.receipt.yes")}
          </NeuButton>
          <NeuButton disabled={busy} data-testid="bonus-receipt-no-btn"
                     onClick={() => (rejecting ? act(false) : setRejecting(true))}
                     className="flex items-center justify-center gap-2 bg-destructive/15 py-3 font-black text-destructive">
            <XCircle className="h-4 w-4" /> {t(rejecting ? "bonus.receipt.noConfirm" : "bonus.receipt.no")}
          </NeuButton>
        </div>
        <button type="button" data-testid="bonus-receipt-later-btn" onClick={onLater}
                className="text-xs font-bold text-muted-foreground underline">
          {t("bonus.receipt.later")}
        </button>
      </NeuCard>
    </div>,
    document.body,
  );
};
